import { useEffect, useRef } from "react";
import { prefersReducedMotion, useInView } from "../hooks";

// Gold, rose and ivory to match the invitation, with a touch of sky blue.
const PALETTE = ["#ffd36e", "#ffe7a8", "#ff8fb5", "#ff4f7b", "#ffb38a", "#fff4dc", "#9fd4ff"];
const GRAVITY = 0.045;
const pick = (list) => list[Math.floor(Math.random() * list.length)];
const rand = (min, max) => min + Math.random() * (max - min);

/**
 * Fireworks over the palace, drawn on a transparent canvas so the night sky shows
 * through. Rockets rise from behind the palace and burst to either side of the
 * centred text. It runs while `active` and on screen, or with `eager` as soon as it
 * is `active`, so the sky is already alight when it scrolls into view (and opens
 * with a volley of bursts the moment it does). With `lowBursts`, a share of the bursts
 * go off lower in the sky, near the horizon, so some stay in view however far it scrolls.
 */
const Fireworks = ({ active, eager = false, lowBursts = false, className = "" }) => {
  const ref = useRef(null);
  const visible = useInView(ref, { threshold: 0, once: false });
  const seen = useRef(visible);
  seen.current = visible;
  const run = eager || visible;

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas || !active || !run || prefersReducedMotion()) return undefined;
    const ctx = canvas.getContext("2d");
    let width = 0;
    let height = 0;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const rockets = [];
    const sparks = [];
    let nextLaunch = 0;
    let volleyed = false;
    let last = performance.now();
    let frame = 0;

    const launch = () => {
      const narrow = width < 700;
      const side = Math.random() < 0.5 ? -1 : 1;
      const x = narrow ? rand(0.12, 0.88) * width : width * (0.5 + side * rand(0.2, 0.44));
      const low = lowBursts && Math.random() < 0.45;
      const targetY = height * (low ? rand(0.46, 0.64) : narrow ? rand(0.06, 0.26) : rand(0.1, 0.42));
      const startY = height * 0.92;
      rockets.push({
        x,
        y: startY,
        vx: rand(-0.4, 0.4),
        vy: -Math.sqrt(2 * GRAVITY * (startY - targetY)),
        color: pick(PALETTE),
      });
    };

    const burst = (x, y, color) => {
      const scale = Math.min(1.3, Math.max(0.7, height / 800));
      const count = Math.round(rand(70, 110));
      const second = Math.random() < 0.35 ? pick(PALETTE) : color;
      const ring = Math.random() < 0.3;
      for (let i = 0; i < count; i += 1) {
        const angle = (Math.PI * 2 * i) / count + rand(-0.05, 0.05);
        const speed = (ring ? 3.2 : rand(0.8, 3.6)) * scale;
        sparks.push({
          x,
          y,
          px: x,
          py: y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          life: rand(55, 95),
          age: 0,
          size: rand(1.3, 2.3),
          color: i % 3 === 0 ? second : color,
          glitter: Math.random() < 0.25,
        });
      }
    };

    const tick = (now) => {
      const dt = Math.min(3, (now - last) / 16.67);
      last = now;

      // Fade the previous frame to leave short trails, keeping the canvas transparent.
      ctx.globalCompositeOperation = "destination-out";
      ctx.fillStyle = "rgba(0, 0, 0, 0.2)";
      ctx.fillRect(0, 0, width, height);
      ctx.globalCompositeOperation = "lighter";

      // The moment the sky comes into view, a volley already bursting across it
      if (seen.current && !volleyed) {
        volleyed = true;
        const narrow = width < 700;
        for (let k = 0; k < (narrow ? 2 : 3); k += 1) {
          const side = k % 2 ? -1 : 1;
          const x = narrow ? width * (0.25 + 0.5 * k + rand(-0.08, 0.08)) : width * (0.5 + side * rand(0.22, 0.42));
          const y = lowBursts && k === 0 ? rand(0.46, 0.6) : narrow ? rand(0.1, 0.24) : rand(0.14, 0.36);
          burst(x, height * y, pick(PALETTE));
        }
      }

      if (now >= nextLaunch) {
        launch();
        if (Math.random() < 0.25) launch();
        nextLaunch = now + rand(520, 1150);
      }

      for (let i = rockets.length - 1; i >= 0; i -= 1) {
        const r = rockets[i];
        const px = r.x;
        const py = r.y;
        r.x += r.vx * dt;
        r.y += r.vy * dt;
        r.vy += GRAVITY * dt;
        ctx.strokeStyle = "rgba(255, 230, 180, 0.8)";
        ctx.lineWidth = 1.6;
        ctx.beginPath();
        ctx.moveTo(px, py);
        ctx.lineTo(r.x, r.y);
        ctx.stroke();
        if (r.vy >= -0.4) {
          burst(r.x, r.y, r.color);
          rockets.splice(i, 1);
        }
      }

      for (let i = sparks.length - 1; i >= 0; i -= 1) {
        const s = sparks[i];
        s.px = s.x;
        s.py = s.y;
        s.vx *= 0.982 ** dt;
        s.vy = s.vy * 0.982 ** dt + GRAVITY * 0.6 * dt;
        s.x += s.vx * dt;
        s.y += s.vy * dt;
        s.age += dt;
        const fade = 1 - s.age / s.life;
        if (fade <= 0) {
          sparks.splice(i, 1);
          continue;
        }
        const alpha = s.glitter ? fade * (0.4 + 0.6 * Math.abs(Math.sin(s.age * 0.6))) : fade;
        ctx.globalAlpha = alpha;
        ctx.strokeStyle = s.color;
        ctx.lineWidth = s.size;
        ctx.lineCap = "round";
        ctx.beginPath();
        ctx.moveTo(s.px, s.py);
        ctx.lineTo(s.x, s.y);
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", resize);
      ctx.clearRect(0, 0, width, height);
    };
  }, [active, run, lowBursts]);

  return <canvas ref={ref} className={`rm-fireworks ${className}`} aria-hidden="true" />;
};

export default Fireworks;
